import { memo, useState, useCallback, useEffect } from 'react';
import { Handle, Position } from 'reactflow';
import { Pencil, X, Loader2, CheckCircle, Play, Download, Eye, Plus, ImagePlus, RefreshCw } from 'lucide-react';
import { downloadFile } from '../../utils/downloadFile';
import { blobToFile } from '../../utils/mediaUtils';
import { useWorkflowStore } from '../../store/workflowStore';
import { useNodeExecution } from '../../hooks/useNodeExecution';
import { useWorkflowTrigger } from '../../hooks/useWorkflowTrigger';
import NodeError from './NodeError';
import NodeResetButton from './NodeResetButton';
import {
  ReconfigureButton, ReconfigureNotice, useReconfigure,
} from './ReconfigureControls';
import { useMediaPreview } from '../../hooks/useMediaPreview';
import { setSourcePickerHandler } from '../../utils/sourcePickerBridge';
import SendToTimelineButton from '../SendToTimelineButton';
import DirectorsTrigger from '../studio/DirectorsTrigger';
import PromptField from '../studio/PromptField';
import NodeRequirements from './NodeRequirements';
import { missingOf, requirementsTitle, textNeed } from '../../utils/nodeRequirements';

const ASPECT_RATIOS = [
  { value: 'match_input_image', label: 'Original' },
  { value: '1:1', label: '1:1' },
  { value: '3:4', label: '3:4' },
  { value: '4:3', label: '4:3' },
  { value: '9:16', label: '9:16' },
  { value: '16:9', label: '16:9' },
];

const INPUT_HANDLES = [
  { id: 'input1', top: '25%' },
  { id: 'input2', top: '50%' },
  { id: 'input3', top: '75%' },
];

const ImageEditNode = memo(({ id, data, selected }) => {
  const [chronometer, setChronometer] = useState(0);
  const [prompt, setPrompt] = useState(data.prompt || '');
  const [images, setImages] = useState(data.images || []);
  const [aspectRatio, setAspectRatio] = useState(data.aspectRatio || 'match_input_image');
  const [turbo, setTurbo] = useState(data.turbo ?? true);

  const updateNodeData = useWorkflowStore((s) => s.updateNodeData);
  const deleteNode = useWorkflowStore((s) => s.deleteNode);
  const addAsset = useWorkflowStore((s) => s.addAsset);
  const edges = useWorkflowStore((s) => s.edges);
  const nodes = useWorkflowStore((s) => s.nodes);
  const openSourcePicker = useWorkflowStore((s) => s.openSourcePicker);

  const { processNode, status: execStatus, error: execError, reset: resetExec, insufficientCredits } = useNodeExecution();
  const { openImagePreview } = useMediaPreview();
  const isProcessing = execStatus === 'uploading' || execStatus === 'queued' || execStatus === 'processing';

  // Connected inputs from handles
  const connectedInputs = INPUT_HANDLES.map(h => {
    const edge = edges.find(e => e.target === id && e.targetHandle === h.id);
    if (!edge) return null;
    const src = nodes.find(n => n.id === edge.source);
    return src?.data?.hasOutput ? src.data : null;
  });
  const connectedCount = connectedInputs.filter(Boolean).length;

  useEffect(() => {
    updateNodeData(id, { prompt, images, aspectRatio, turbo });
  }, [prompt, aspectRatio, turbo]);

  const handleAddImage = useCallback(() => {
    if (images.length >= 3) return;
    setSourcePickerHandler((result) => {
      if (result.file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setImages(prev => {
            const updated = [...prev, { file: result.file, preview: e.target.result, name: result.file.name }];
            updateNodeData(id, { images: updated });
            return updated;
          });
        };
        reader.readAsDataURL(result.file);
      } else if (result.url) {
        setImages(prev => {
          const updated = [...prev, { file: null, preview: result.url, name: result.fileName }];
          updateNodeData(id, { images: updated });
          return updated;
        });
      }
    });
    openSourcePicker(id, ['image'], 'image/jpeg,image/jpg,image/png,image/webp');
  }, [id, images.length, openSourcePicker, updateNodeData]);

  const removeImage = useCallback((index) => {
    setImages(prev => {
      const updated = prev.filter((_, i) => i !== index);
      updateNodeData(id, { images: updated });
      return updated;
    });
  }, [id, updateNodeData]);

  useEffect(() => {
    let interval;
    if (isProcessing) {
      setChronometer(0);
      interval = setInterval(() => setChronometer(p => p + 1), 1000);
    }
    return () => { if (interval) clearInterval(interval); };
  }, [isProcessing]);

  const formatTime = (s) => `${Math.floor(s/60).toString().padStart(2,'0')}:${(s%60).toString().padStart(2,'0')}`;

  // Regenerar descarta el resultado; volver a configurar lo conserva.
  const { reconfigure, setReconfigure, showForm } = useReconfigure(data);

  const handleProcess = useCallback(async () => {
    if (!prompt.trim()) return false;
    resetExec();

    const files = [];
    const directUrls = [];

    // Collect from connected handles (first image = main image for editing)
    for (const input of connectedInputs) {
      if (!input) continue;
      const fileUrl = input.outputUrl || input.preview;
      if (input.file) {
        files.push({ key: `image_${files.length}`, file: input.file });
      } else if (fileUrl?.startsWith('http')) {
        directUrls.push(fileUrl);
      } else if (fileUrl) {
        const f = await blobToFile(fileUrl, input.fileName || 'img.jpg', 'image/jpeg');
        if (f) files.push({ key: `image_${files.length}`, file: f });
      }
    }

    // Collect manually added images
    for (const img of images) {
      if (img.file) {
        files.push({ key: `image_${files.length}`, file: img.file });
      } else if (img.preview?.startsWith('http')) {
        directUrls.push(img.preview);
      } else if (img.preview) {
        const f = await blobToFile(img.preview, img.name || 'img.jpg', 'image/jpeg');
        if (f) files.push({ key: `image_${files.length}`, file: f });
      }
    }

    const params = {
      prompt: prompt.trim(),
      turbo,
      aspect_ratio: aspectRatio,
      image_count: files.length + directUrls.length,
    };
    directUrls.forEach((url, i) => { params[`image_${files.length + i}`] = url; });

    const usedPrompt = prompt.trim();
    const result = await processNode({ resultType: 'image_edit', files, params });

    if (result?.outputUrl) {
      setReconfigure(false);
      updateNodeData(id, {
        hasOutput: true,
        outputUrl: result.outputUrl,
        assetId: result.assetId ?? null,
        lastPrompt: usedPrompt,
        processedAt: new Date().toISOString(),
      });
      addAsset({ id: `asset_${Date.now()}`, type: 'image', tool: 'imageEdit', filename: `edit_${Date.now()}.jpg`, url: result.outputUrl, prompt: usedPrompt, createdAt: new Date().toISOString() });
      return true;
    }
    return false;
  }, [id, prompt, images, connectedInputs, turbo, aspectRatio, updateNodeData, addAsset, processNode, resetExec]);

  const handleRegenerate = useCallback(() => {
    updateNodeData(id, { hasOutput: false, outputUrl: null });
    resetExec();
  }, [id, updateNodeData, resetExec]);

  // "Volver a configurar": formulario con todo lo elegido, resultado intacto.

  const { isInQueue, isMyTurn } = useWorkflowTrigger(id, handleProcess);
  const totalImages = images.length + connectedCount;

  // NR-1 · qué le falta al nodo para generar (se muestra debajo del botón)
  const runMissing = missingOf([textNeed(prompt, 'escribir qué querés cambiar en la imagen')]);

  return (
    <div className={`node-container imageEdit-node ${selected ? 'selected' : ''} ${isInQueue ? 'workflow-queued' : ''} ${isMyTurn ? 'workflow-active' : ''}`}>
      <NodeResetButton nodeId={id} hasOutput={!!data.hasOutput} />

      {INPUT_HANDLES.map(h => (
        <Handle key={h.id} type="target" position={Position.Left} id={h.id} className="node-handle input-handle" style={{ top: h.top }} />
      ))}

      <div className="node-header imageEdit-header">
        <div className="node-header-left">
          <Pencil size={16} />
          <span>Image Edit</span>
        </div>
        <button className="node-delete-btn" onClick={() => deleteNode(id)} data-testid="delete-image-edit">
          <X size={14} />
        </button>
      </div>

      <div className="node-content">
        {isProcessing ? (
          <div className="node-processing">
            <div className="node-chronometer">{formatTime(chronometer)}</div>
            <div className="node-processing-info">
              <Loader2 size={16} className="animate-spin" />
              <span>{execStatus === 'uploading' ? 'Subiendo...' : execStatus === 'queued' ? 'En cola...' : 'Editando imagen...'}</span>
            </div>
          </div>
        ) : !showForm ? (
          <div className="node-result nano-result-view">
            <img src={data.outputUrl} alt="Editada" className="node-result-image clickable" onClick={() => openImagePreview(data.outputUrl, 'Image Edit')} />
            <div className="node-result-actions">
              <button className="node-mini-preview-btn" onClick={() => openImagePreview(data.outputUrl, 'Image Edit')} data-testid="imgedit-preview-btn"><Eye size={12} /> Ver</button>
              <button className="node-mini-download-btn" onClick={(e) => { e.stopPropagation(); downloadFile(data.outputUrl, `edit_${Date.now()}.jpg`); }} data-testid="imgedit-download-btn"><Download size={12} /> Descargar</button>
              <SendToTimelineButton assetId={data.assetId} nodeId={id} url={data.outputUrl} kind="image" label="imageedit" testIdPrefix="imageedit-timeline" />
            </div>
            <div className="nano-regen-section">
              <span className="nano-section-label">Prompt usado:</span>
              <PromptField value={prompt} onChange={setPrompt} nodeId={id} nodeType="imageEdit" label="Prompt de regeneración" className="nano-prompt-textarea nano-regen-textarea" rows={2} testId="imgedit-regen-prompt" />
              <button className="nano-regen-btn" onClick={handleRegenerate} data-testid="imgedit-regen-btn">
                <RefreshCw size={12} /> Regenerar
              </button>
              <ReconfigureButton
                onClick={() => setReconfigure(true)}
                testId="imgedit-reconfigure-btn"
              />
            </div>
          </div>
        ) : (
          <>
            {data.outputUrl && reconfigure && (
              <ReconfigureNotice
                previewUrl={data.outputUrl}
                onCancel={() => setReconfigure(false)}
                testId="imgedit-reconfigure-notice"
              />
            )}
            {connectedCount > 0 && (
              <div className="node-input-status connected">
                <CheckCircle size={12} />
                <span>{connectedCount} imagen{connectedCount > 1 ? 'es' : ''} conectada{connectedCount > 1 ? 's' : ''}</span>
              </div>
            )}

            <div className="nano-images-section">
              <span className="nano-section-label"><ImagePlus size={12} /> Imágenes ({totalImages}/3) — primera = principal</span>
              <div className="nano-images-row">
                {images.map((img, i) => (
                  <div key={img.name || `img-${i}`} className="nano-img-thumb">
                    <img src={img.preview} alt={`Ref ${i+1}`} />
                    <button className="nano-clear-btn" onClick={() => removeImage(i)} data-testid={`imgedit-remove-img-${i}`}><X size={8} /></button>
                  </div>
                ))}
                {images.length < 3 && (
                  <button className="nano-add-img-btn" onClick={handleAddImage} data-testid="imgedit-add-image"><Plus size={14} /></button>
                )}
              </div>
            </div>

            <div className="nano-prompt-section">
              <div className="nano-prompt-header">
                <span className="nano-section-label">Prompt de edición *</span>
                <DirectorsTrigger nodeId={id} nodeType="imageEdit" currentPrompt={prompt} onInsert={setPrompt} />
              </div>
              <PromptField value={prompt} onChange={setPrompt} nodeId={id} nodeType="imageEdit" placeholder="Describe la edición: 'Change dress to red', 'Add sunglasses'..." className="nano-prompt-textarea" rows={3} testId="imgedit-prompt" />
            </div>

            <div className="nano-options-row">
              <div className="nano-option">
                <span className="nano-option-label">Ratio</span>
                <select value={aspectRatio} onChange={(e) => setAspectRatio(e.target.value)} className="nano-select" data-testid="imgedit-aspect-ratio">
                  {ASPECT_RATIOS.map(ar => <option key={ar.value} value={ar.value}>{ar.label}</option>)}
                </select>
              </div>
              <div className="nano-option">
                <label className="imageEdit-turbo-label">
                  <input type="checkbox" checked={turbo} onChange={(e) => setTurbo(e.target.checked)} data-testid="imgedit-turbo" />
                  <span>Turbo</span>
                </label>
              </div>
            </div>

            <button className="node-process-btn imageEdit-process-btn" onClick={handleProcess} disabled={!prompt.trim()} title={requirementsTitle(runMissing)} data-testid="imgedit-process-btn">
              <Play size={14} /> {data.outputUrl && reconfigure ? 'Regenerar con cambios' : 'Editar Imagen'}
            </button>
            <NodeRequirements missing={runMissing} />
          </>
        )}
        <NodeError error={execError} insufficientCredits={insufficientCredits} />
      </div>

      <Handle type="source" position={Position.Right} id="output" className="node-handle output-handle" isConnectable={!!data.hasOutput} />
    </div>
  );
});

ImageEditNode.displayName = 'ImageEditNode';
export default ImageEditNode;
